import { useCallback, useEffect, useRef, useState } from "react";

// Keep recent conversations visible while refreshing and ignore obsolete requests.
export default function useChatMessages(phone, enabled, apiFetch) {
  const cache = useRef(new Map());
  const sessionRef = useRef(null);
  const [view, setView] = useState({ phone: null, data: null, loading: false, loadingOlder: false, error: "" });

  useEffect(() => {
    if (!enabled || !phone) {
      sessionRef.current = null;
      if (!enabled) cache.current.clear();
      setView({ phone: null, data: null, loading: false, loadingOlder: false, error: "" });
      return;
    }
    const session = { data: cache.current.get(phone) || null, disposed: false, busy: false,
      loadingOlder: false, error: "", controllers: new Set(), refreshAgain: false };
    sessionRef.current = session;
    const publish = () => {
      if (session.disposed) return;
      if (session.data) {
        cache.current.delete(phone);
        cache.current.set(phone, session.data);
        if (cache.current.size > 10) cache.current.delete(cache.current.keys().next().value);
      }
      setView({ phone, data: session.data, loading: !session.data && session.busy,
        loadingOlder: session.loadingOlder, error: session.error });
    };
    const request = async suffix => {
      const controller = new AbortController();
      session.controllers.add(controller);
      try {
        const data = await apiFetch(`/messages/${encodeURIComponent(phone)}?limit=100${suffix}`, { signal: controller.signal });
        if (!Array.isArray(data.messages) || !Number.isInteger(data.start)) throw new Error(data.error || "Unable to load messages.");
        return data;
      } finally { session.controllers.delete(controller); }
    };
    session.refresh = async () => {
      if (session.disposed) return;
      if (session.busy) { session.refreshAgain = true; return; }
      session.busy = true;
      session.error = "";
      publish();
      try {
        const latest = await request("");
        if (session.disposed) return;
        const previous = session.data;
        // Retain older loaded pages when the latest page overlaps them.
        const next = previous && latest.total >= previous.total && latest.start >= previous.start && latest.start <= previous.start + previous.messages.length
          ? { ...latest, start: previous.start, hasMore: previous.start > 0,
            messages: [...previous.messages.slice(0, latest.start - previous.start), ...latest.messages] }
          : latest;
        // Unchanged polls should not re-render or scroll the conversation.
        if (JSON.stringify(previous) !== JSON.stringify(next)) session.data = next;
      } catch (err) { if (!session.disposed) session.error = err.message || "Unable to load messages."; }
      finally {
        session.busy = false;
        publish();
        if (session.refreshAgain && !session.disposed) { session.refreshAgain = false; session.refresh(); }
      }
    };
    session.loadOlder = async () => {
      if (session.disposed || session.loadingOlder || !session.data?.hasMore) return;
      const before = session.data.start;
      session.loadingOlder = true;
      session.error = "";
      publish();
      try {
        const older = await request(`&before=${before}`);
        if (session.disposed) return;
        if (session.data.start === before) session.data = { ...session.data, start: older.start,
          hasMore: older.hasMore, messages: [...older.messages, ...session.data.messages] };
      } catch (err) { if (!session.disposed) session.error = err.message || "Unable to load older messages."; }
      finally { session.loadingOlder = false; publish(); }
    };
    session.refresh();
    const timer = setInterval(session.refresh, 5000);
    return () => {
      session.disposed = true;
      clearInterval(timer);
      session.controllers.forEach(controller => controller.abort());
    };
  }, [phone, enabled, apiFetch]);

  const refresh = useCallback(() => sessionRef.current?.refresh(), []);
  const loadOlder = useCallback(() => sessionRef.current?.loadOlder(), []);
  const current = enabled && view.phone === phone ? view : {
    data: enabled ? cache.current.get(phone) || null : null, loading: enabled && !!phone, error: "", loadingOlder: false,
  };
  return { messages: current.data?.messages || [], start: current.data?.start,
    hasMore: current.data?.hasMore || false, loading: current.loading, loadingOlder: current.loadingOlder,
    error: current.error, refresh, loadOlder };
}
